/**
 * Explorer Sync Service
 * Syncs Object Explorer selection when context is resolved via toolbar actions
 */

import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import {
  createConnectionNodeId,
  createDatabasesFolderNodeId,
  createDatabaseNodeId,
} from '../../explorer/utils/nodeIdUtils';
import { findNodeById } from '../../explorer/services/treeService';
import type { ResolvedContext } from '../types/contextTypes';

type SyncExplorerSelectionParams = {
  context: ResolvedContext;
  /** If true, skips sync when context matches current selection */
  skipIfAlreadySelected?: boolean;
};

/**
 * Syncs the Object Explorer selection with the resolved context.
 * - Updates useDatabaseUIStore selection (connectionId, database, nodeId)
 * - Expands connection node and databases folder
 * - Expands database node if database is selected
 *
 * @returns true if selection was synced, false if skipped
 */
export function syncExplorerSelection({
  context,
  skipIfAlreadySelected = true,
}: SyncExplorerSelectionParams): boolean {
  const { connectionId, database } = context;

  // Get current selection state
  const uiStore = useDatabaseUIStore.getState();
  const {
    selectedConnectionId: currentConnectionId,
    selectedDatabase: currentDatabase,
  } = uiStore;

  // Skip if context already matches current selection
  if (skipIfAlreadySelected) {
    const connectionMatches = currentConnectionId === connectionId;
    const databaseMatches = database
      ? currentDatabase === database
      : true; // If no database required, only check connection

    if (connectionMatches && databaseMatches) {
      return false;
    }
  }

  // Get database store for tree operations
  const dbStore = useDatabaseStore.getState();
  const { treeData, toggleNodeExpanded, updateNode } = dbStore;

  // Create node IDs
  const connectionNodeId = createConnectionNodeId(connectionId);
  const databasesFolderNodeId = createDatabasesFolderNodeId(connectionId);

  // 1. Expand connection node if not expanded
  const connectionNode = findNodeById(connectionNodeId, treeData);
  if (connectionNode && !connectionNode.isExpanded) {
    toggleNodeExpanded(connectionNodeId);
  }

  // 2. Expand databases folder if not expanded
  const databasesFolder = findNodeById(databasesFolderNodeId, treeData);
  if (databasesFolder && !databasesFolder.isExpanded) {
    // Databases folder is usually auto-expanded, but ensure it is
    updateNode(databasesFolderNodeId, { isExpanded: true });
  }

  // 3. If database is selected, expand database node and set selection
  let nodeIdToSelect = connectionNodeId;

  if (database) {
    const databaseNodeId = createDatabaseNodeId(connectionId, database);
    const databaseNode = findNodeById(databaseNodeId, treeData);

    // Always select the database node ID so toolbar-driven context resolution can
    // reflect the chosen database even if the explorer tree hasn't loaded it yet.
    nodeIdToSelect = databaseNodeId;

    if (databaseNode && !databaseNode.isExpanded) {
      toggleNodeExpanded(databaseNodeId);
    }
  }

  // 4. Update UI store selection
  // setSelectedNodeId handles setting all related state (connectionId, database, etc.)
  uiStore.setSelectedNodeId(nodeIdToSelect);

  // 5. Set highlight for scroll-into-view and animation
  uiStore.setHighlightedNodeId(nodeIdToSelect);

  return true;
}

/**
 * Checks if the current selection matches the given context.
 * Used to determine if sync is needed.
 */
export function isContextAlreadySelected(context: ResolvedContext): boolean {
  const { connectionId, database } = context;
  const uiStore = useDatabaseUIStore.getState();

  const connectionMatches = uiStore.selectedConnectionId === connectionId;

  if (!database) {
    // If no database required, just check connection
    return connectionMatches;
  }

  return connectionMatches && uiStore.selectedDatabase === database;
}
